import { api } from 'api';
import { FUNC_DEFAULT } from 'constants';
import { checkMaintenance } from 'helpers/maintenance';
import useAuth from 'hooks/useAuth';
import useCommonStore from 'hooks/useCommonStore';
import { isEmpty } from 'lodash';

const useReceiverAPI = () => {
  const { token } = useAuth();
  const { currentReceiver, setLoading } = useCommonStore();

  const validatePhoneRequest = async ({
    countryCode = '',
    number = '',
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!countryCode) return null;
    if (!number) return null;

    setLoading(true);
    try {
      const { data } = await api.validatePhone(countryCode, number);
      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ validatePhoneRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getCitiesRequest = async ({
    countryCode = '',
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!countryCode) return null;

    setLoading(true);
    try {
      const { data } = await api.getCities(countryCode);
      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ getCitiesRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getReceiverRequest = async ({
    receiverId,
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId) return null;

    setLoading(true);
    try {
      const { data } = await api.getReceiver(token, receiverId);
      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ getReceiverRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateReceiverNameGenderRequest = async ({
    receiverId = '',
    firstname = '',
    lastname = '',
    gender = '',
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId) return null;
    if (!firstname) return null;
    if (!gender) return null;

    setLoading(true);
    try {
      const payload = {
        firstname,
        lastname,
        gender,
      };
      console.log(
        `⭐ useReceiverAPI 👉 updateReceiverNameGenderRequest ➡️ payload ↩`,
        payload,
      );
      const { data } = await api.updateDetails(token, receiverId, payload);
      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ updateReceiverNameGenderRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const CONTACT_TYPES = ['mobile', 'phone'];
  const resolveAction = ({ mobilePhoneNumber }) => {
    if (!mobilePhoneNumber || isEmpty(mobilePhoneNumber)) return 'delete';
    if (!mobilePhoneNumber?.id || mobilePhoneNumber?.id === 0) return 'add';
    return 'update';
  };
  const updateReceiverMobilePhoneNumberRequest = async ({
    receiverId = '',
    mobilePhoneNumber = null,
    type = '',
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId || !CONTACT_TYPES.includes(type)) return null;

    const { id, number } = mobilePhoneNumber || {};
    const actionType = resolveAction({ mobilePhoneNumber });

    setLoading(true);

    try {
      let response;

      const payload = number
        ? { id: id || 0, isValidated: true, number }
        : null;

      const ACTIONS = {
        delete: () =>
          api.deleteContact(
            token,
            receiverId,
            id || currentReceiver?.[type]?.id,
          ),
        add: () => api.addContact(token, receiverId, type, payload),
        update: () => api.updateContact(token, receiverId, id, payload),
      };

      response = await ACTIONS[actionType]();

      const { data } = response;

      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ updateReceiverMobilePhoneNumberRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateReceiverRelationshipRequest = async ({
    receiverId = '',
    relationship = '',
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId) return null;

    setLoading(true);
    try {
      console.log(
        `⭐ useReceiverAPI 👉 updateReceiverRelationshipRequest ➡️ relationship ↩`,
        relationship,
      );
      const { data } = await api.updateRelationship(
        token,
        receiverId,
        relationship,
      );
      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ updateReceiverRelationshipRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateReceiverAddressRequest = async ({
    receiverId = '',
    address = null,
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId) return null;
    if (isEmpty(address)) return null;

    setLoading(true);
    try {
      console.log(
        `⭐ useReceiverAPI 👉 updateReceiverAddressRequest ➡️ payload ↩`,
        address,
      );
      const { data } = await api.updateAddress(
        token,
        receiverId,
        address?.id,
        address,
      );
      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ updateReceiverAddressRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const BANK_ACTION_REQUEST_REF = {
    add: async ({ api, token, receiverId, bank }) => {
      const payload = {
        ...bank,
        accountName: bank?.accountName?.toUpperCase(),
      };
      delete payload.id;

      return api.addBank(token, receiverId, payload);
    },

    update: async ({ api, token, receiverId, bank }) => {
      return api.updateBank(token, receiverId, bank?.id, bank);
    },

    delete: async ({ api, token, receiverId, bank }) => {
      return api.deleteBank(token, receiverId, bank?.id);
    },
  };
  const updateReceiverBankRequest = async ({
    receiverId = '',
    bank = null,
    type = '', // add | update | delete
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId || !bank || !BANK_ACTION_REQUEST_REF[type]) return null;

    setLoading(true);
    try {
      console.log(
        `⭐ useReceiverAPI 👉 updateReceiverBanksRequest ➡️ payload ${type} ↩`,
        bank,
      );
      const action = BANK_ACTION_REQUEST_REF[type];

      const { data } = await action({
        api,
        token,
        receiverId,
        bank,
      });

      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ updateReceiverBanksRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const EWALLET_ACTION_REQUEST_REF = {
    add: async ({ api, token, receiverId, ewallet }) => {
      const payload = {
        ...ewallet,
      };
      delete payload.id;

      return api.addEwallet(token, receiverId, payload);
    },

    update: async ({ api, token, receiverId, ewallet }) => {
      return api.updateEwallet(token, receiverId, ewallet?.id, ewallet);
    },

    delete: async ({ api, token, receiverId, ewallet }) => {
      return api.deleteEwallet(token, receiverId, ewallet?.id);
    },
  };
  const updateReceiverEwalletRequest = async ({
    receiverId = '',
    ewallet = null,
    type = '', // add | update | delete
    callBack = FUNC_DEFAULT,
  } = {}) => {
    if (!receiverId || !ewallet || !EWALLET_ACTION_REQUEST_REF[type])
      return null;

    setLoading(true);
    try {
      console.log(
        `⭐ useReceiverAPI 👉 updateReceiverEwalletRequest ➡️ payload ${type} ↩`,
        ewallet,
      );
      const action = EWALLET_ACTION_REQUEST_REF[type];

      const { data } = await action({
        api,
        token,
        receiverId,
        ewallet,
      });

      callBack(data);
      return data;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useReceiverAPI ❌ updateReceiverEwalletRequest ➡️ ERROR ↩`,
        error?.message,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    validatePhoneRequest,
    getCitiesRequest,
    getReceiverRequest,
    updateReceiverNameGenderRequest,
    updateReceiverMobilePhoneNumberRequest,
    updateReceiverRelationshipRequest,
    updateReceiverAddressRequest,
    updateReceiverBankRequest,
    updateReceiverEwalletRequest,
  };
};

export default useReceiverAPI;
